// <Button> — shared button component per DESIGN.md §7.1.
//
// Variants:   primary | secondary | ghost | danger
// Sizes:      sm (28px) | md (36px) | lg (44px)
// Loading:    renders a mono "…" spinner; disables click
// IconLeft/Right: 16px at sm, 18px at md/lg (left to caller; we just slot)
//
// Notes:
//   - `primary` uses brand blue; `danger` is reserved for destructive
//     confirmations (force-logout, revoke grant).
//   - Disabled is 40% opacity per §7.1.
//   - Focus ring uses :focus-visible only (no outline:none regression).

import type { ButtonHTMLAttributes, ReactNode } from "react";
import "./Button.css";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  className?: string;
}

export function Button(props: ButtonProps): JSX.Element {
  const {
    variant = "secondary",
    size = "md",
    loading = false,
    iconLeft,
    iconRight,
    disabled,
    children,
    type,
    className,
    ...rest
  } = props;

  const classes = [
    "rb-btn",
    `rb-btn--${variant}`,
    `rb-btn--${size}`,
    loading ? "rb-btn--loading" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type={type ?? "button"}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {iconLeft && (
        <span className="rb-btn__icon rb-btn__icon--left" aria-hidden="true">
          {iconLeft}
        </span>
      )}
      <span className="rb-btn__label">{children}</span>
      {loading && (
        <span className="rb-btn__spinner" aria-hidden="true">
          …
        </span>
      )}
      {iconRight && !loading && (
        <span className="rb-btn__icon rb-btn__icon--right" aria-hidden="true">
          {iconRight}
        </span>
      )}
    </button>
  );
}
